import React from "react";
import {
    Form,
    Input,
    Button,
    Card,
    Typography,
    Alert,
    Progress,
    Space,
} from "antd";
import Layout from "../../components/layout/Layout";


import {
    FiGrid,        // Dashboard
    FiTarget,      // My predictions
    FiBell,        // Notifications
    FiUser,        // Profile
    FiCreditCard,  // Billing
    FiLayers,      // Plans
    FiLock,        // Change password
    FiShield,      // Security
    FiInfo,        // Tip
    FiCheckCircle, // Rule OK
    FiXCircle,    // Rule not met
    FiKey,         // Current password
    FiArrowLeft,   // Back
} from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { getErrorMessage } from "../../helpers/ApiResponseHelper";
import MemberServiceInstance from "../../services/MemberService";
import { setFormFieldValue } from "../../helpers/FormHelper";
import { useSelector } from "react-redux";

const LINKS = [
    { to: "/dashboard", label: "Dashboard", Icon: FiGrid },
    { to: "/my/predictions", label: "My predictions", Icon: FiTarget },
    { to: "/notifications", label: "Notifications", Icon: FiBell },
    { to: "/account/profile", label: "My profile", Icon: FiUser },
    { to: "/account/billing", label: "Billing", Icon: FiCreditCard },
    { to: "/plans", label: "Plans & Pricing", Icon: FiLayers },
    { to: "/account/security", label: "Change password", Icon: FiLock },
];

const { Title, Text } = Typography;

function scorePassword(pw = "") {
    let score = 0;
    if (pw.length >= 8) score += 25;
    if (/[A-Z]/.test(pw)) score += 20;
    if (/[a-z]/.test(pw)) score += 15;
    if (/\d/.test(pw)) score += 20;
    if (/[^A-Za-z0-9]/.test(pw)) score += 20;
    if (pw.length >= 12) score += 10;

    if (pw.length === 0) return { pct: 0, label: "Start typing", status: "normal" };
    if (score < 40) return { pct: Math.min(score, 40), label: "Weak", status: "exception" };
    if (score < 70) return { pct: Math.min(score, 70), label: "Fair", status: "active" };
    if (score < 90) return { pct: Math.min(score, 90), label: "Good", status: "active" };
    return { pct: 100, label: "Strong", status: "success" };
}

function Rule({ ok, children }) {
    const Icon = ok ? FiCheckCircle : FiXCircle;
    return (
        <li className="px-rule">
            <Icon size={16} className={ok ? "ok" : "bad"} />
            <span>{children}</span>
        </li>
    );
}

export default function AccountSecurity() {
    const [form] = Form.useForm();
    const token = useSelector((state) => state.Auth.token);
    const [submitting, setSubmitting] = React.useState(false);
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [successMsg, setSuccessMsg] = React.useState(null);

    const { pathname } = useLocation();
    const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

    const current = Form.useWatch("currentPassword", form) || "";
    const nextPw = Form.useWatch("newPassword", form) || "";
    const confirmPw = Form.useWatch("confirmPassword", form) || "";

    const strength = scorePassword(nextPw);

    const rulesState = React.useMemo(
        () => ({
            len: nextPw.length >= 8,
            upper: /[A-Z]/.test(nextPw),
            lower: /[a-z]/.test(nextPw),
            digit: /\d/.test(nextPw),
            symbol: /[^A-Za-z0-9]/.test(nextPw),
            notSameAsCurrent: !!nextPw && current && nextPw !== current,
            matchesConfirm: !!nextPw && confirmPw && nextPw === confirmPw,
        }),
        [nextPw, confirmPw, current]
    );

    const formatRequestData = (data) => {
        return {
            oldPassword: setFormFieldValue(data?.currentPassword),
            newPassword: setFormFieldValue(data?.newPassword),
        };
    };

    const onSubmit = async () => {
        setErrorMsg(null);
        setSuccessMsg(null);
        try {
            setSubmitting(true);
            const data = await form.validateFields();
            const requestData = formatRequestData(data);
            const response = await MemberServiceInstance.changePassword(token, requestData);
            const responseData = response.data;
            const { success, message } = responseData;
            if (success === true) {
                setSuccessMsg('Your password changed successfully!');
                form.resetFields();
            } else {
                setErrorMsg(message);
            }
        } catch (error) {
            if (error?.values) {
                setSubmitting(false);
                return;
            }
            const errorMessage = getErrorMessage(error?.response, error?.message);
            setErrorMsg(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const ui = {
        heroWrap: {
            position: "relative",
            padding: "80px 16px 80px",
            background:
                "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,.06)",
        },
        heroInner: { maxWidth: 1350, margin: "0 auto" },
        badge: {
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 12, color: "#93c5fd", background: "rgba(147,197,253,.10)",
            border: "1px solid rgba(147,197,253,.30)", padding: "6px 12px", borderRadius: 999,
        },
        heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: '#fff', letterSpacing: "-0.02em" },
        heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
        subnavWrap: {
            position: "sticky", top: 0, zIndex: 20, backdropFilter: "blur(6px)",
            background: "rgba(255,255,255,.8)", borderBottom: `1px solid rgba(2,6,23,.08)`,
        },
        subnavInner: { maxWidth: 1400, margin: "0 auto", padding: "10px 16px" },
        subnavList: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
    };

    return (
        <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Account Security">
            <section style={ui.heroWrap} aria-label="Hero">
                <div style={ui.heroInner}>
                    <span style={ui.badge}><FiShield /> Account Security</span>
                    <h1 style={ui.heroTitle}>Change Password</h1>
                    <p style={ui.heroSub}>
                        Keep your account safe by using a unique, strong password. Avoid reusing passwords across different services.
                    </p>
                </div>
            </section>
            <nav style={ui.subnavWrap} className={`px-subnav`} aria-label="Section navigation">
                <div className="px-subnav__inner">
                    {LINKS.map(({ to, label, Icon }) => (
                        <Link key={to} to={to} className={`px-subnav__link ${isActive(to) ? "is-active" : ""}`}>
                            <Icon aria-hidden />
                            <span>{label}</span>
                        </Link>
                    ))}
                </div>
            </nav>
            <div className="px-secure">
                <div className="px-wrap">
                    {/* Info / Hero */}
                    <aside className="px-side">
                        <div className="px-side-card">
                            <div className="px-badge">
                                <FiShield />
                                <span>Security</span>
                            </div>
                            <Title level={2} className="px-title">
                                Change password
                            </Title>
                            <Text type="secondary" className="px-desc">
                                Keep your account safe by using a unique, strong password. Avoid reusing passwords across different services.
                            </Text>

                            <div className="px-tips">
                                <FiInfo className="tip-ico" />
                                <div>
                                    <b>Tip:</b> Use at least 12 characters with a mix of upper/lowercase, numbers, and symbols.
                                </div>
                            </div>

                            <ul className="px-rules">
                                <Rule ok={rulesState.len}>At least 8 characters</Rule>
                                <Rule ok={rulesState.upper}>Contains an uppercase letter (A-Z)</Rule>
                                <Rule ok={rulesState.lower}>Contains a lowercase letter (a-z)</Rule>
                                <Rule ok={rulesState.digit}>Contains a number (0-9)</Rule>
                                <Rule ok={rulesState.symbol}>Contains a symbol (!@#…)</Rule>
                                <Rule ok={rulesState.notSameAsCurrent}>Different from current password</Rule>
                                <Rule ok={rulesState.matchesConfirm}>Matches confirmation</Rule>
                            </ul>
                        </div>
                    </aside>

                    <main className="px-main register-modal">
                        <Card className="px-card" bordered>
                            <Space align="center" size={10} style={{ marginBottom: 12 }}>
                                <FiLock />
                                <Text strong>Update your credentials</Text>
                            </Space>

                            {errorMsg && (
                                <Alert
                                    type="error"
                                    showIcon
                                    message={errorMsg}
                                    style={{ marginBottom: 12 }}
                                    closable
                                    onClose={() => setErrorMsg(null)}
                                />
                            )}
                            {successMsg && (
                                <Alert
                                    type="success"
                                    showIcon
                                    message={successMsg}
                                    style={{ marginBottom: 12 }}
                                    closable
                                    onClose={() => setSuccessMsg(null)}
                                />
                            )}

                            <Form
                                form={form}
                                layout="vertical"
                                autoComplete="off"
                                onFinish={onSubmit}
                                requiredMark={false}
                            >
                                <Form.Item
                                    label={
                                        <div className="px-row">
                                            <span style={{ fontWeight: 'bold' }}>Current password</span>
                                        </div>
                                    }
                                    name="currentPassword"
                                    rules={[{ required: true, message: "Please enter your current password" }]}
                                >
                                    <Input.Password
                                        size="large"
                                        prefix={<FiKey />}
                                        placeholder="Enter current password"
                                        autoComplete="current-password"
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={
                                        <div className="px-row">
                                            <span style={{ fontWeight: 'bold' }}>New password</span>
                                        </div>
                                    }
                                    name="newPassword"
                                    rules={[
                                        { required: true, message: "Please enter a new password" },
                                        { min: 8, message: "Must be at least 8 characters" },
                                        () => ({
                                            validator(_, value) {
                                                if (!value) return Promise.resolve();
                                                if (!/[A-Z]/.test(value)) return Promise.reject("Add an uppercase letter");
                                                if (!/[a-z]/.test(value)) return Promise.reject("Add a lowercase letter");
                                                if (!/\d/.test(value)) return Promise.reject("Add a number");
                                                if (!/[^A-Za-z0-9]/.test(value)) return Promise.reject("Add a symbol");
                                                if (value === form.getFieldValue("currentPassword"))
                                                    return Promise.reject("New password must differ from current");
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        size="large"
                                        prefix={<FiLock />}
                                        placeholder="Create a strong password"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                                <span className="px-strength">
                                    <Progress
                                        percent={strength.pct}
                                        size="small"
                                        status={strength.status}
                                        showInfo={false}
                                        strokeWidth={8}
                                        style={{ width: '100%' }}
                                    />
                                </span>
                                <span className={`px-strength-label ${strength.status}`}>

                                </span>

                                <Form.Item
                                    label={
                                        <div className="px-row">
                                            <span style={{ fontWeight: 'bold' }}>Confirm new password</span>
                                        </div>
                                    }
                                    style={{ marginTop: 12 }}
                                    name="confirmPassword"
                                    dependencies={["newPassword"]}
                                    rules={[
                                        { required: true, message: "Please confirm your new password" },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue("newPassword") === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject("Passwords do not match");
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        size="large"
                                        prefix={<FiLock />}
                                        placeholder="Re-enter new password"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>

                                <div className="px-actions">
                                    <Button
                                        type="default"
                                        size="large"
                                        icon={<FiArrowLeft />}
                                        href="/account/profile"
                                    >
                                        Back to profile
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<FiShield />}
                                        loading={submitting}
                                        onClick={onSubmit}
                                    >
                                        Save new password
                                    </Button>
                                </div>
                            </Form>
                        </Card>
                    </main>
                </div>

                <style>{`
        .px-secure { background:#f7f7f9; padding:30px; }
        .px-wrap {
          max-width: 1400px; margin: 0 auto;
          display: grid; gap: 20px;
          grid-template-columns: 420px 1fr;
        }
        @media (max-width: 1024px) {
          .px-wrap { grid-template-columns: 1fr; }
        }
        .px-side-card {
          background:#fff; border:1px solid rgba(0,0,0,.08);
          border-radius:16px; padding:22px;
          box-shadow: 0 8px 24px rgba(0,0,0,.05);
        }
        .px-badge {
          display:inline-flex; align-items:center; gap:8px;
          font-size:12px; color:#1e3a8a;
          background:rgba(37,99,235,.10);
          border:1px solid rgba(37,99,235,.25);
          padding:6px 10px; border-radius:999px;
          margin-bottom:10px;
        }
        .px-title { margin: 6px 0 4px !important; }
        .px-desc { display:block; }
        .px-tips {
          margin-top:14px; display:flex; gap:8px; align-items:flex-start;
          background:linear-gradient(180deg,#fff,#fafafa);
          border:1px dashed rgba(0,0,0,.15); border-radius:12px; padding:10px;
          color:#334155; font-size:13px;
        }
        .px-tips .tip-ico { margin-top:2px; opacity:.8; }

        .px-rules { margin:14px 0 0; padding-left:0; list-style:none; display:grid; gap:8px; }
        .px-rule { display:flex; align-items:center; gap:8px; font-size:13px; color:#475569; }
        .px-rule .ok { color:#16a34a; }
        .px-rule .bad { color:#ef4444; }

        .px-card { border-radius:16px; }
        .px-row { display:flex; align-items:center; justify-content:space-between; gap:10px; }
        .px-strength { display:flex; align-items:center; gap:8px; }
        .px-strength-label { font-size:12px; color:#64748b; }
        .px-strength-label.success { color:#16a34a; }
        .px-strength-label.exception { color:#ef4444; }

        .px-subnav {
          position: sticky; top: 0; z-index: 20;
          backdrop-filter: blur(6px);
          background: rgba(255,255,255,.85);
          border-bottom: 1px solid rgba(2,6,23,.08);
        }
        .px-subnav__inner {
          max-width: 1400px; margin: 0 auto;
          padding: 10px 16px;
          display: flex; gap: 8px; align-items: center; flex-wrap: nowrap;
          overflow-x: auto; -webkit-overflow-scrolling: touch;
        }
        .px-subnav__link {
          display: inline-flex; align-items: center;
          padding: 8px 12px; gap: 8px;
          border-radius: 999px;
          font-size: 13px; font-weight: 700;
          color: #334155;
          border: 1px solid transparent;
          white-space: nowrap;
        }
        .px-subnav__link:hover {
          background: #f1f5f9;
          border-color: rgba(2,6,23,.08);
        }
        .px-subnav__link.is-active {
          color: #1e3a8a;
          background: rgba(37,99,235,.10);
          border-color: rgba(37,99,235,.25);
        }
        @media (max-width: 480px) {
          .px-subnav__inner { gap: 6px; padding: 8px 12px; }
          .px-subnav__link { padding: 6px 10px; font-weight: 600; }
        }

        .px-actions {
          margin-top:6px; display:flex; gap:12px; justify-content:flex-end; flex-wrap:wrap;
        }
        @media (max-width: 480px) {
          .px-secure { padding:16px; }
          .px-actions { flex-direction:column; align-items:stretch; }
        }
      `}</style>
            </div>
        </Layout>
    );
}
